/**
 * Program (relic) content registry.
 *
 * Programs warp the rules to enable + reward each deck archetype:
 *   Aggro/Spike · Armor/Counter · Virus/DoT · Chain/Combo · Draw-engine/Economy
 *
 * Every Program is pure declarative data: static `mods` (read at combat setup)
 * and/or `triggers` (fired by the program engine at hooks). No closures, so the
 * whole set is serializable + unit-testable + replayable.
 *
 * Rarities: starter (character keystone) · common · uncommon · rare · boss
 * (granted only for defeating an act boss; the strongest, build-defining relics).
 */

import type { ProgramDef } from "../types";

export const PROGRAM_DEFS: Record<string, ProgramDef> = {
  // ===========================================================================
  // STARTER keystones (character identity)
  // ===========================================================================
  ghost_protocol: {
    id: "ghost_protocol",
    name: "Ghost Protocol",
    rarity: "starter",
    tags: ["virus", "control"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "reduceTrace", value: 2 }],
      },
    ],
    mods: { traceMaxDelta: 2 },
    description:
      "Ghost's keystone. Start each fight with -2 Trace; your Trace ceiling is +2 higher. Patience is a weapon.",
  },
  brute_loader: {
    id: "brute_loader",
    name: "Brute Loader",
    rarity: "starter",
    tags: ["aggro", "armor"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainStatusSelf", status: "strength", amount: 1 }],
      },
    ],
    mods: { startingBlock: 5 },
    description:
      "Breaker's keystone. Begin each fight with 1 Strength and 5 Block. Kick the door in.",
  },

  // ===========================================================================
  // ECONOMY / DRAW ENGINE
  // ===========================================================================
  cycle_capacitor: {
    id: "cycle_capacitor",
    name: "Cycle Capacitor",
    rarity: "common",
    tags: ["economy"],
    mods: { cyclesPerTurn: 0 },
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainCycles", value: 1 }],
      },
    ],
    description: "Gain 1 extra Cycle on the first turn of each combat.",
  },
  bandwidth_chip: {
    id: "bandwidth_chip",
    name: "Bandwidth Chip",
    rarity: "common",
    tags: ["draw", "economy"],
    mods: { handSize: 1 },
    triggers: [],
    description: "Draw 1 additional card each turn (and 1 more in your opening hand).",
  },
  compiler: {
    id: "compiler",
    name: "Compiler",
    rarity: "uncommon",
    tags: ["economy"],
    mods: { cyclesPerTurn: 1, traceMaxDelta: -2 },
    triggers: [],
    description:
      "+1 Cycle every turn — but you run hotter: your Trace ceiling is 2 lower.",
  },
  prefetch: {
    id: "prefetch",
    name: "Prefetch Buffer",
    rarity: "uncommon",
    tags: ["draw", "combo"],
    triggers: [
      {
        hook: "cardPlayed",
        effects: [{ kind: "draw", value: 1, everyN: 3 }],
      },
    ],
    description: "Every 3rd card you play this combat, draw 1 card.",
  },
  overflow_stack: {
    id: "overflow_stack",
    name: "Overflow Stack",
    rarity: "rare",
    tags: ["economy", "combo"],
    triggers: [
      {
        hook: "turnStart",
        effects: [{ kind: "draw", value: 2 }],
      },
      {
        hook: "turnStart",
        effects: [{ kind: "addTrace", value: 1 }],
      },
    ],
    description:
      "Draw 2 extra cards at the start of each turn — but the noise adds 1 Trace each turn.",
  },
  daemon_thread: {
    id: "daemon_thread",
    name: "Daemon Thread",
    rarity: "uncommon",
    tags: ["economy", "power"],
    triggers: [
      {
        hook: "powerPlayed",
        effects: [{ kind: "draw", value: 1 }],
      },
    ],
    description: "Whenever you play a Power, draw 1 card.",
  },

  // ===========================================================================
  // AGGRO / SPIKE
  // ===========================================================================
  gain_amp: {
    id: "gain_amp",
    name: "Gain Amplifier",
    rarity: "common",
    tags: ["aggro"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainStatusSelf", status: "strength", amount: 1 }],
      },
    ],
    description: "Start each combat with 1 Strength.",
  },
  overdrive_core: {
    id: "overdrive_core",
    name: "Overdrive Core",
    rarity: "rare",
    tags: ["aggro"],
    triggers: [
      {
        hook: "turnStart",
        effects: [{ kind: "gainStatusSelf", status: "strength", amount: 1 }],
      },
    ],
    description: "Gain 1 Strength at the start of every turn. It snowballs.",
  },
  killchain: {
    id: "killchain",
    name: "Killchain",
    rarity: "uncommon",
    tags: ["aggro", "combo"],
    triggers: [
      {
        hook: "enemyKilled",
        effects: [
          { kind: "gainCycles", value: 1 },
          { kind: "draw", value: 1 },
        ],
      },
    ],
    description: "When an ICE dies, gain 1 Cycle and draw 1 card. Snowball through packs.",
  },
  glass_cannon: {
    id: "glass_cannon",
    name: "Glass Cannon",
    rarity: "rare",
    tags: ["aggro"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainStatusSelf", status: "strength", amount: 3 }],
      },
    ],
    mods: { traceMaxDelta: -4 },
    description:
      "Start each combat with 3 Strength — but your Trace ceiling is 4 lower. Win fast or lose.",
  },
  power_spike: {
    id: "power_spike",
    name: "Power Spike",
    rarity: "uncommon",
    tags: ["aggro", "combo"],
    triggers: [
      {
        hook: "attackPlayed",
        effects: [{ kind: "dealDamageTarget", value: 2, everyN: 3 }],
      },
    ],
    description: "Every 3rd attack you play this combat deals 2 extra damage to its target's pack.",
  },

  // ===========================================================================
  // ARMOR / COUNTER
  // ===========================================================================
  plating: {
    id: "plating",
    name: "Reactive Plating",
    rarity: "common",
    tags: ["armor"],
    mods: { startingBlock: 6 },
    triggers: [],
    description: "Gain 6 Block at the start of each combat.",
  },
  kinetic_buffer: {
    id: "kinetic_buffer",
    name: "Kinetic Buffer",
    rarity: "uncommon",
    tags: ["armor", "counter"],
    triggers: [
      {
        hook: "blockGained",
        effects: [{ kind: "dealDamageAll", value: 0, scaleByCtxAmount: 0.5 }],
      },
    ],
    description:
      "When you gain Block, deal damage to all ICE equal to half the Block gained. Defense IS offense.",
  },
  hardened_shell: {
    id: "hardened_shell",
    name: "Hardened Shell",
    rarity: "uncommon",
    tags: ["armor"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainStatusSelf", status: "firewall", amount: 2 }],
      },
    ],
    description: "Start each combat with 2 Firewall (reduce every incoming hit by 2).",
  },
  counterspike: {
    id: "counterspike",
    name: "Counterspike",
    rarity: "rare",
    tags: ["armor", "counter"],
    triggers: [
      {
        hook: "hpLost",
        effects: [{ kind: "gainStatusSelf", status: "strength", amount: 1 }],
      },
    ],
    description: "Whenever you take HP damage, gain 1 Strength. Make them regret hitting you.",
  },
  static_field: {
    id: "static_field",
    name: "Static Field",
    rarity: "uncommon",
    tags: ["armor", "counter"],
    triggers: [
      {
        hook: "blockGained",
        effects: [{ kind: "applyStatusAll", status: "weak", amount: 1, everyN: 2 }],
      },
    ],
    description: "Every 2nd time you gain Block, apply 1 Weak to all ICE.",
  },

  // ===========================================================================
  // VIRUS / DoT
  // ===========================================================================
  viral_load: {
    id: "viral_load",
    name: "Viral Load",
    rarity: "uncommon",
    tags: ["virus"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "applyStatusAll", status: "virus", amount: 3 }],
      },
    ],
    description: "Inject 3 Virus into every ICE at the start of combat.",
  },
  contagion_engine: {
    id: "contagion_engine",
    name: "Contagion Engine",
    rarity: "rare",
    tags: ["virus"],
    triggers: [
      {
        hook: "turnEnd",
        effects: [{ kind: "applyStatusAll", status: "poison", amount: 1 }],
      },
    ],
    description: "At the end of each turn, apply 1 Poison to every ICE. The rot never stops.",
  },
  necrosis: {
    id: "necrosis",
    name: "Necrosis",
    rarity: "uncommon",
    tags: ["virus"],
    triggers: [
      {
        hook: "skillPlayed",
        effects: [{ kind: "applyStatusAll", status: "poison", amount: 1, ifCardTag: "virus" }],
      },
    ],
    description: "When you play a Virus skill, also apply 1 Poison to all ICE.",
  },
  plague_vector: {
    id: "plague_vector",
    name: "Plague Vector",
    rarity: "rare",
    tags: ["virus", "combo"],
    triggers: [
      {
        hook: "enemyKilled",
        effects: [{ kind: "applyStatusAll", status: "virus", amount: 4 }],
      },
    ],
    description: "When an ICE dies, spread 4 Virus to all surviving ICE. Cascade the outbreak.",
  },

  // ===========================================================================
  // CHAIN / COMBO
  // ===========================================================================
  combo_buffer: {
    id: "combo_buffer",
    name: "Combo Buffer",
    rarity: "uncommon",
    tags: ["combo"],
    triggers: [
      {
        hook: "cardPlayed",
        effects: [{ kind: "gainBlock", value: 1 }],
      },
    ],
    description: "Whenever you play a card, gain 1 Block. Rewards long turns.",
  },
  chain_reactor: {
    id: "chain_reactor",
    name: "Chain Reactor",
    rarity: "rare",
    tags: ["combo", "aggro"],
    triggers: [
      {
        hook: "cardPlayed",
        effects: [{ kind: "dealDamageAll", value: 1 }],
      },
    ],
    description: "Whenever you play a card, deal 1 damage to all ICE. Big turns end fights.",
  },
  momentum: {
    id: "momentum",
    name: "Momentum",
    rarity: "uncommon",
    tags: ["combo"],
    triggers: [
      {
        hook: "cardPlayed",
        effects: [{ kind: "gainStatusSelf", status: "dexterity", amount: 1, everyN: 4 }],
      },
    ],
    description: "Every 4th card you play this combat, gain 1 Dexterity.",
  },
  feedback_loop: {
    id: "feedback_loop",
    name: "Feedback Loop",
    rarity: "rare",
    tags: ["combo", "economy"],
    triggers: [
      {
        hook: "cardPlayed",
        effects: [{ kind: "gainCycles", value: 1, everyN: 5 }],
      },
    ],
    description: "Every 5th card you play this combat, refund 1 Cycle. Keep the chain alive.",
  },

  // ===========================================================================
  // TRACE / CONTROL
  // ===========================================================================
  signal_scrambler: {
    id: "signal_scrambler",
    name: "Signal Scrambler",
    rarity: "common",
    tags: ["control"],
    mods: { tracePerTurnDelta: -1 },
    triggers: [],
    description: "Reduce passive Trace gain by 1 each turn. Buy yourself time.",
  },
  cold_boot: {
    id: "cold_boot",
    name: "Cold Boot",
    rarity: "uncommon",
    tags: ["control"],
    triggers: [
      {
        hook: "turnStart",
        effects: [{ kind: "reduceTrace", value: 1 }],
      },
    ],
    description: "Lower Trace by 1 at the start of each turn.",
  },
  faraday_cage: {
    id: "faraday_cage",
    name: "Faraday Cage",
    rarity: "rare",
    tags: ["control", "armor"],
    mods: { traceMaxDelta: 6 },
    triggers: [],
    description: "Raise your Trace ceiling by 6. Far more room before the hard countdown.",
  },
  proxy_chain: {
    id: "proxy_chain",
    name: "Proxy Chain",
    rarity: "uncommon",
    tags: ["control", "virus"],
    triggers: [
      {
        hook: "traceGained",
        effects: [{ kind: "applyStatusAll", status: "poison", amount: 1, everyN: 3 }],
      },
    ],
    description:
      "Every 3rd time Trace rises, route the heat back: apply 1 Poison to all ICE.",
  },

  // ===========================================================================
  // UTILITY / SUSTAIN
  // ===========================================================================
  nanorepair: {
    id: "nanorepair",
    name: "Nanorepair",
    rarity: "common",
    tags: ["control"],
    triggers: [
      {
        hook: "turnStart",
        effects: [{ kind: "heal", value: 1 }],
      },
    ],
    description: "Heal 1 HP at the start of each turn.",
  },
  blood_cache: {
    id: "blood_cache",
    name: "Blood Cache",
    rarity: "uncommon",
    tags: ["economy"],
    triggers: [
      {
        hook: "enemyKilled",
        effects: [{ kind: "heal", value: 3 }],
      },
    ],
    description: "Heal 3 HP whenever an ICE dies.",
  },
  scavenger: {
    id: "scavenger",
    name: "Scavenger Module",
    rarity: "common",
    tags: ["economy"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "draw", value: 1 }],
      },
    ],
    description: "Draw 1 extra card on the first turn of each combat.",
  },
  adrenal_chip: {
    id: "adrenal_chip",
    name: "Adrenal Chip",
    rarity: "uncommon",
    tags: ["aggro", "economy"],
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainCycles", value: 2 }],
      },
      {
        hook: "combatStart",
        effects: [{ kind: "addTrace", value: 2 }],
      },
    ],
    description: "Burst open: +2 Cycles turn one, but +2 Trace. Front-load your damage.",
  },

  // ===========================================================================
  // BOSS relics (strongest; build-defining; granted for act-boss kills)
  // ===========================================================================
  black_ice: {
    id: "black_ice",
    name: "Black ICE",
    rarity: "boss",
    tags: ["aggro"],
    mods: { cyclesPerTurn: 1 },
    triggers: [
      {
        hook: "combatStart",
        effects: [{ kind: "gainStatusSelf", status: "strength", amount: 2 }],
      },
    ],
    description:
      "BOSS. +1 Cycle every turn and start each combat with 2 Strength. Raw, unfiltered power.",
  },
  quantum_core: {
    id: "quantum_core",
    name: "Quantum Core",
    rarity: "boss",
    tags: ["economy", "combo"],
    mods: { handSize: 2, tracePerTurnDelta: 1 },
    triggers: [],
    description:
      "BOSS. Draw 2 more cards every turn — but Trace rises 1 faster. Engine, unleashed.",
  },
  null_suite: {
    id: "null_suite",
    name: "Null Suite",
    rarity: "boss",
    tags: ["virus", "control"],
    triggers: [
      {
        hook: "turnStart",
        effects: [{ kind: "applyStatusAll", status: "virus", amount: 2 }],
      },
    ],
    mods: { traceMaxDelta: 3 },
    description:
      "BOSS. Inject 2 Virus into every ICE each turn; +3 Trace ceiling. The slow, certain death.",
  },
  aegis_protocol: {
    id: "aegis_protocol",
    name: "Aegis Protocol",
    rarity: "boss",
    tags: ["armor", "counter"],
    mods: { startingBlock: 10, innateStatus: { firewall: 1 } },
    triggers: [
      {
        hook: "turnStart",
        effects: [{ kind: "gainBlock", value: 4 }],
      },
    ],
    description:
      "BOSS. Start with 10 Block + 1 Firewall, and gain 4 Block every turn. An immovable wall.",
  },
  recursion: {
    id: "recursion",
    name: "Recursion",
    rarity: "boss",
    tags: ["combo", "economy"],
    triggers: [
      {
        hook: "cardPlayed",
        effects: [{ kind: "gainCycles", value: 1, everyN: 3 }],
      },
      {
        hook: "cardPlayed",
        effects: [{ kind: "draw", value: 1, everyN: 6 }],
      },
    ],
    description:
      "BOSS. Every 3rd card refunds 1 Cycle; every 6th also draws a card. Chains that never end.",
  },
  apex_predator: {
    id: "apex_predator",
    name: "Apex Predator",
    rarity: "boss",
    tags: ["aggro", "combo"],
    triggers: [
      {
        hook: "enemyKilled",
        effects: [
          { kind: "gainStatusSelf", status: "strength", amount: 1 },
          { kind: "heal", value: 4 },
        ],
      },
    ],
    description:
      "BOSS. Each ICE you kill makes you permanently stronger (+1 Strength) and heals 4 HP. Feed.",
  },
};

export function getProgramDef(id: string): ProgramDef {
  const def = PROGRAM_DEFS[id];
  if (!def) throw new Error(`Unknown program def: ${id}`);
  return def;
}

/** All program ids (stable, sorted) — used by reward pools + tests. */
export function allProgramIds(): string[] {
  return Object.keys(PROGRAM_DEFS).sort();
}

/** Program ids eligible for normal (non-boss, non-starter) reward pools. */
export function rewardProgramIds(): string[] {
  return allProgramIds().filter((id) => {
    const r = PROGRAM_DEFS[id]!.rarity;
    return r === "common" || r === "uncommon" || r === "rare";
  });
}

/** Boss-only program ids. */
export function bossProgramIds(): string[] {
  return allProgramIds().filter((id) => PROGRAM_DEFS[id]!.rarity === "boss");
}
